<script setup lang="ts">
import { ref, watch, computed, provide } from 'vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faChevronUp } from '@fortawesome/pro-solid-svg-icons';
import { useMediaQuery } from '@vueuse/core';
import ContactBoxActions from '@/shared/components/ContactBox/ContactBoxActions.vue';
import ContactBoxAvatarRating from '@/shared/components/ContactBox/ContactBoxAvatarRating.vue';

interface Props {
  linkedProfile?: boolean;
}

withDefaults(defineProps<Props>(), {
  linkedProfile: true,
});

const MOBILE_BREAKPOINT = '(max-width: 768px)' as const;
const ANIMATION_DURATION = 300;
const MAX_CONTENT_HEIGHT = '125px';

const isMobile = useMediaQuery(MOBILE_BREAKPOINT);
const isAvatarVisible = ref(true);

const chevronClasses = computed(() => ({
  'rotate-180': isAvatarVisible.value,
  'rotate-0': !isAvatarVisible.value,
  'transform transition-transform duration-200': isMobile,
}));

const containerClasses = computed(() => ({
  'sticky bottom-0 bg-white mt-6 z-20 xl:hidden': true,
  'shadow-[0px_-1px_5px_0px_rgba(0,0,0,0.2)]': true,
}));

const toggleButtonClasses = computed(() => ({
  'absolute top-[-15px] right-0': true,
  'w-[25px] h-[18px] bg-white rounded': true,
  'cursor-pointer flex items-center justify-center': true,
  'text-info-500 md:hidden': true,
  'shadow-[0px_-2px_4px_0px_rgba(0,0,0,0.2)]': true,
}));

provide('give-rating-button', 'giveRatingMobile');

watch(isMobile, (newVal) => {
  if (!newVal) {
    isAvatarVisible.value = true;
  }
});

const toggleAvatarVisibility = () => {
  isAvatarVisible.value = !isAvatarVisible.value;
};
</script>

<template>
  <div :class="containerClasses">
    <div
      :class="toggleButtonClasses"
      @click="toggleAvatarVisibility"
      role="button"
      aria-label="Sichtbarkeit des Avatars umschalten">
      <FontAwesomeIcon :icon="faChevronUp" :class="chevronClasses" />
    </div>

    <div class="md:flex items-center gap-6 md:justify-between px-4 pt-4 xl:pt-0">
      <transition name="slide" :duration="ANIMATION_DURATION">
        <div v-if="isAvatarVisible">
          <ContactBoxAvatarRating :linkedProfile="linkedProfile" :hasInlineRating="true" :hasMobileFontSize="true" />
        </div>
      </transition>
      <ContactBoxActions />
    </div>
  </div>
</template>

<style scoped>
.slide-enter-active,
.slide-leave-active {
  transition: all v-bind(ANIMATION_DURATION + 'ms') ease;
  max-height: v-bind(MAX_CONTENT_HEIGHT);
  overflow: hidden;
}

.slide-enter-from,
.slide-leave-to {
  max-height: 0;
  opacity: 0;
  transform: translateY(-10px);
}

.slide-enter-to,
.slide-leave-from {
  max-height: v-bind(MAX_CONTENT_HEIGHT);
  opacity: 1;
  transform: translateY(0);
}
</style>
